import Button from "components/Button";
import bankingService, {
  BANK_AUTH_STATE_KEY,
  getBankingRedirectUrl,
} from "api/bankingService";
import styles from "./styles.module.scss";

interface Props {
  /** Etykieta banku do wyświetlenia (np. „mBank"). */
  bankName: string;
  /** Nazwa banku (ASPSP) w katalogu Enable Banking. */
  aspspName: string;
  /** Kraj ASPSP (ISO-3166 alpha-2), domyślnie „PL". */
  country?: string;
  onClose: () => void;
}

/**
 * Kreator integracji z systemem bankowym w standardzie PolishAPI
 * (PSD2 / Open Banking, autoryzacja OAuth2). Połączenie realizowane jest przez
 * konektor TPP — **Enable Banking** — wspólną warstwą `bankingService` (tą samą,
 * z której korzysta rejestr „Operacje finansowe"). Po kliknięciu „Połącz"
 * użytkownik jest przekierowywany do ekranu logowania/zgody w swoim banku (SCA);
 * po powrocie zgoda jest finalizowana w rejestrze Operacje finansowe.
 *
 * Patrz docs/backend-bank-open-banking.md.
 */
const STEPS = [
  "Wybór banku z listy obsługiwanych instytucji.",
  "Przekierowanie do banku i wyrażenie zgody PSD2 (logowanie w banku).",
  "Pobranie danych rachunku i transakcji w ramach udzielonej zgody.",
];

const BankIntegrationWizard = ({
  bankName,
  aspspName,
  country = "PL",
  onClose,
}: Props) => {
  const startAuth = bankingService.useStartAuth();

  const handleConnect = () => {
    startAuth.mutate(
      {
        AspspName: aspspName,
        AspspCountry: country,
        RedirectUrl: getBankingRedirectUrl(),
      },
      {
        onSuccess: (res) => {
          sessionStorage.setItem(BANK_AUTH_STATE_KEY, res.State);
          window.location.assign(res.AuthorizationUrl);
        },
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.bank}>{bankName}</div>

      <div className={styles.note}>
        Połączenie odbywa się w standardzie <strong>PolishAPI</strong> (PSD2 /
        Open Banking) za pośrednictwem konektora <strong>Enable Banking</strong>.
      </div>

      <div className={styles.stepsTitle}>Przebieg integracji</div>
      <ol className={styles.steps}>
        {STEPS.map((s, i) => (
          <li key={i} className={styles.step}>
            {s}
          </li>
        ))}
      </ol>

      {startAuth.isLoading && (
        <div className={styles.pending}>
          Trwa nawiązywanie połączenia… Za chwilę nastąpi przekierowanie do
          ekranu logowania banku.
        </div>
      )}

      {startAuth.isError && (
        <div className={styles.pending}>
          Nie udało się rozpocząć połączenia. Spróbuj ponownie.
        </div>
      )}

      <div className={styles.actions}>
        <Button
          className={styles.connectBtn}
          text={`Połącz z ${bankName}`}
          loading={startAuth.isLoading}
          onClick={handleConnect}
        />
        <button className={styles.cancelBtn} onClick={onClose}>
          Anuluj
        </button>
      </div>
    </div>
  );
};

export default BankIntegrationWizard;
